import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Form, InputNumber, Modal, Select, Switch, Typography } from 'antd';
import type { AdminPrice, AdminProduct, FormaDePagamento } from '@titularis/api-client';
import { api } from '../../api';
import { rotuloDoPlano } from '@titularis/ui';
import { condicaoDoPreco, emReais, FORMA_LABEL } from '../../dominio';

/**
 * O preço de uma forma de pagamento.
 *
 * **Não edita: substitui.** A rota insere uma linha nova e desativa a anterior
 * da mesma forma, e o texto do modal diz isso - quem muda o preço precisa saber
 * que a venda de ontem continua explicada pelo valor de ontem.
 *
 * O valor é digitado em reais e sai em centavos, que é como o banco guarda.
 * A conversão passa por `Math.round` de propósito: `84,9 * 100` em ponto
 * flutuante é 8489,999..., e um truncamento silencioso venderia por um centavo
 * a menos.
 */
interface Props {
  aberto: boolean;
  produto: AdminProduct;
  /**
   * O preço sendo mudado; ausente ao estrear um plano.
   *
   * **O preço inteiro, e não só a forma de pagamento.** Desde que duração e
   * recorrência entraram, a forma sozinha não identifica uma linha - o mesmo
   * produto tem cartão mensal, cartão semestral e cartão anual ativos ao mesmo
   * tempo, e abrir o modal por "cartão" carregaria um plano arbitrário.
   */
  precoInicial?: AdminPrice;
  aoFechar: () => void;
}

interface Campos {
  forma: FormaDePagamento;
  meses: number;
  recorrente: boolean;
  reais: number;
  parcelas: number;
}

/** Acima disto, só Visa e Mastercard parcelam - as demais bandeiras param em 12. */
const PARCELAS_SEM_RESSALVA = 12;
const MAXIMO_DE_PARCELAS = 21;

/**
 * Teto da duração, e é folga e não regra.
 *
 * Trinta e seis meses não é um plano que exista; é o ponto a partir do qual um
 * número digitado é quase certamente engano - um "120" onde se queria "12".
 */
const MAXIMO_DE_MESES = 36;

export function FormularioDePreco({ aberto, produto, precoInicial, aoFechar }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Campos>();

  const forma = Form.useWatch('forma', form);
  const meses = Form.useWatch('meses', form);
  const recorrente = Form.useWatch('recorrente', form);
  const reais = Form.useWatch('reais', form);
  const parcelas = Form.useWatch('parcelas', form);

  /*
   * O que já está à venda **neste mesmo plano**, que é o que este salvamento
   * vai substituir. A identidade é a mesma do servidor - forma, duração e
   * recorrência -, e tem de ser: se as duas discordassem, o modal prometeria
   * substituir um preço e o banco substituiria outro.
   */
  const vigente = produto.precos.find(
    (preco) =>
      preco.active &&
      preco.kind === forma &&
      preco.accessMonths === meses &&
      preco.recurring === recorrente,
  );

  useEffect(() => {
    if (!aberto) return;
    form.setFieldsValue({
      /*
       * `kind` é texto no banco de propósito - forma de pagamento é catálogo
       * comercial, e a coluna não é enum para que um provedor novo não custe
       * migração. O estreitamento acontece aqui, na borda: o formulário só
       * sabe oferecer as duas que existem hoje.
       */
      forma: precoInicial?.kind === 'card' ? 'card' : 'pix',
      meses: precoInicial?.accessMonths ?? 12,
      recorrente: precoInicial?.recurring ?? false,
      reais: precoInicial ? precoInicial.amountCents / 100 : undefined,
      parcelas: precoInicial?.maxInstallments ?? 1,
    });
  }, [aberto, precoInicial, form]);

  /*
   * Recorrência é do cartão. No Asaas, assinatura em Pix não debita o ciclo
   * seguinte - ela gera outra cobrança para o cliente pagar na mão -, e o
   * servidor recusa. Desligar aqui ao trocar para Pix evita mandar uma
   * combinação que só volta como erro.
   */
  useEffect(() => {
    if (forma !== 'card' && recorrente) form.setFieldsValue({ recorrente: false });
  }, [forma, recorrente, form]);

  const salvar = useMutation({
    mutationFn: (campos: Campos) =>
      api.setPrice(produto.id, {
        forma: campos.forma,
        accessMonths: campos.meses,
        recurring: campos.forma === 'card' && campos.recorrente,
        amountCents: Math.round(campos.reais * 100),
        /*
         * Só o cartão parcela, e assinatura não parcela nunca: o ciclo já é a
         * divisão do pagamento, e parcelar um plano que se repete cobraria
         * duas divisões da mesma coisa.
         */
        maxInstallments: campos.forma === 'card' && !campos.recorrente ? campos.parcelas : 1,
      }),
    onSuccess: async () => {
      message.success('Preço definido.');
      await queryClient.invalidateQueries({ queryKey: ['produtos'] });
      await queryClient.invalidateQueries({ queryKey: ['produto', produto.id] });
      aoFechar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const centavos = Math.round((reais ?? 0) * 100);
  const parcelasEfetivas = forma === 'card' && !recorrente ? (parcelas ?? 1) : 1;

  return (
    <Modal
      open={aberto}
      title={
        vigente
          ? `Mudar o preço ${rotuloDoPlano(vigente.accessMonths).toLowerCase()} de ${FORMA_LABEL[vigente.kind]}`
          : 'Definir preço'
      }
      okText="Salvar"
      cancelText="Cancelar"
      confirmLoading={salvar.isPending}
      onCancel={aoFechar}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(campos) => salvar.mutate(campos)}
        style={{ marginTop: 16 }}
      >
        <Form.Item name="forma" label="Forma de pagamento" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'pix', label: 'Pix' },
              { value: 'card', label: 'Cartão de crédito' },
            ]}
          />
        </Form.Item>

        {/*
          A duração é digitada, e não escolhida numa lista de três. Mensal,
          semestral e anual são o que se vende hoje, não o que o catálogo
          permite: prender a tela a esses três significaria um deploy para
          experimentar um plano de quatro meses, e preço é decisão que muda sem
          deploy neste produto.
        */}
        <Form.Item
          name="meses"
          label="Duração do acesso"
          rules={[{ required: true, message: 'Quantos meses cada pagamento concede?' }]}
          extra="Quantos meses cada pagamento concede. 1 é mensal, 6 semestral, 12 anual."
        >
          <InputNumber min={1} max={MAXIMO_DE_MESES} suffix="meses" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="recorrente"
          label="Renova sozinho"
          valuePropName="checked"
          extra={
            forma === 'card'
              ? 'O provedor abre uma assinatura e cobra a cada ciclo até o aluno cancelar.'
              : 'Só o cartão renova sozinho: no Pix o provedor gera uma cobrança nova a cada ciclo e o aluno precisa pagá-la na mão.'
          }
        >
          <Switch disabled={forma !== 'card'} />
        </Form.Item>

        {/*
          A explicação segue a forma escolhida. A frase do Pix ficava embaixo do
          preço do cartão quando a forma mudava, dizendo a coisa certa sobre a
          coisa errada.
        */}
        <Form.Item
          name="reais"
          label="Valor"
          rules={[{ required: true, message: 'Quanto custa?' }]}
          extra={
            forma === 'pix'
              ? 'O que o comprador paga à vista. A taxa do Pix é fixa, e é aí que cabe desconto.'
              : 'O total da compra. É sobre ele que o parcelamento é calculado, e a taxa do cartão é percentual.'
          }
        >
          {/*
            `decimalSeparator` e nada mais. Um `formatter`/`parser` próprio para
            pôr o ponto de milhar disputaria a normalização com o separador
            decimal, e é o tipo de disputa que se perde por um centavo - o campo
            é de dinheiro, e o acabamento não vale o risco.
          */}
          <InputNumber<number>
            prefix="R$"
            min={1}
            max={100_000}
            precision={2}
            decimalSeparator=","
            style={{ width: '100%' }}
          />
        </Form.Item>

        {/*
          Parcelamento some na assinatura, e não é enfeite: o ciclo já divide o
          pagamento. Oferecer "6x" num plano que cobra todo mês perguntaria em
          quantas vezes se paga uma parcela.
        */}
        {forma === 'card' && !recorrente && (
          <Form.Item
            name="parcelas"
            label="Parcelas"
            rules={[{ required: true }]}
            extra="Um significa à vista."
          >
            <InputNumber min={1} max={MAXIMO_DE_PARCELAS} style={{ width: '100%' }} />
          </Form.Item>
        )}

        {centavos > 0 && meses > 0 && (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            O comprador vê <b>{emReais(centavos)}</b>{' '}
            {condicaoDoPreco(centavos, parcelasEfetivas)}
            {recorrente ? ', renovando a cada ' : ', por '}
            {meses === 1 ? 'mês' : `${meses} meses`}
            {'. '}
            {/*
              O equivalente mensal é o número que compara planos, e é o único
              jeito de ver que um semestral está caro: R$ 349 ao lado de R$ 519
              não diz nada; R$ 58 ao lado de R$ 43 diz tudo.
            */}
            Equivale a <b>{emReais(Math.round(centavos / meses))}</b> por mês.
          </Typography.Paragraph>
        )}

        {parcelasEfetivas > PARCELAS_SEM_RESSALVA && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Acima de ${PARCELAS_SEM_RESSALVA}x só parcelam Visa e Mastercard.`}
            description="As demais bandeiras param em 12 parcelas, e quem estiver com outro cartão verá menos opções na tela do provedor."
          />
        )}

        {vigente && (
          <Alert
            type="info"
            showIcon
            message={`Hoje: ${emReais(vigente.amountCents)} ${condicaoDoPreco(vigente.amountCents, vigente.maxInstallments)}`}
            description="O preço não é reescrito: o de hoje é desativado e o novo entra no lugar. A venda de ontem continua explicada pelo valor de ontem."
          />
        )}
      </Form>
    </Modal>
  );
}
